import { useEffect, useState } from "react";
import { api, download } from "../api";
import { useAsync } from "../hooks";
import { RefreshButton } from "./RefreshButton";
import { fmt, short } from "../format";

const PAGE_SIZE = 50;

type ActorType = "session" | "apikey" | "admin" | "system";
interface AuditEntry {
  id: string;
  at: string;
  tenantId: string | null;
  actorType: ActorType;
  actorId: string | null;
  actorLabel: string | null;
  action: string;
  method: string;
  route: string;
  targetId: string | null;
  status: number;
  ip: string | null;
}
interface AuditPage {
  entries: AuditEntry[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * The audit trail — every mutating/admin action across the fleet (operator view). Filter + paginate
 * server-side (GET /audit?page&pageSize&q); the search box is debounced. Reads (GET) are never
 * logged, so this page never records itself.
 */
export function AuditLog() {
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setDq(q.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const qs = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
  if (dq) qs.set("q", dq);
  const { data, loading, error, reload } = useAsync<AuditPage>(
    () => api(`/audit?${qs.toString()}`),
    [page, dq],
  );

  if (loading && !data) return <div className="muted pad">Loading…</div>;
  if (error) return <div className="err pad">Error: {error}</div>;
  const rows = data?.entries ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="stack">
      <div className="section-head">
        <h1>Audit</h1>
        <div className="row-gap">
          <input
            className="clients-search"
            placeholder="filter by action / route"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button
            className="btn ghost sm"
            onClick={() => {
              const eq = new URLSearchParams({ format: "csv" });
              if (dq) eq.set("q", dq);
              void download(`/audit?${eq.toString()}`, "audit-log.csv");
            }}
          >
            Export CSV
          </button>
          <RefreshButton onClick={reload} />
        </div>
      </div>

      <p className="fleet-line lead">
        <em>{total}</em> recorded action{total === 1 ? "" : "s"}
        {dq && (
          <>
            {" "}
            matching <em>“{dq}”</em>
          </>
        )}
        <span className="muted"> · every write is logged; reads are not</span>
      </p>

      {total === 0 && <div className="empty">{dq ? "No actions match." : "No actions recorded yet."}</div>}

      {rows.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Target</th>
                <th>Result</th>
                <th>Client</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id}>
                  <td className="nowrap">{fmt(e.at)}</td>
                  <td>
                    {e.actorLabel ?? <span className="mono muted">{short(e.actorId ?? "—")}</span>}
                    <br />
                    <ActorPill type={e.actorType} />
                  </td>
                  <td>
                    <code className="mono">{e.method}</code> <span className="muted mono">{e.route}</span>
                  </td>
                  <td className="mono muted">{e.targetId ? short(e.targetId) : "—"}</td>
                  <td>
                    <span className={e.status >= 400 ? "pill failed" : "pill"}>{e.status}</span>
                  </td>
                  <td className="mono muted">{e.tenantId ? short(e.tenantId) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="pager">
          <button className="btn ghost sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            ← Prev
          </button>
          <span className="muted sm">
            {page * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE + PAGE_SIZE, total)} of {total}
          </span>
          <button
            className="btn ghost sm"
            disabled={page >= pageCount - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

/** admin / apikey stand out (dashed) as elevated or programmatic; a session is the quiet default. */
function ActorPill({ type }: { type: ActorType }) {
  if (type === "session") return <span className="muted sm">session</span>;
  return <span className="pill hold">{type}</span>;
}
